import { ReactiveMap } from "@solid-primitives/map"
import { batch, createEffect } from "solid-js"
import { WaterFlowErrorTypeId } from "../../lib/core/Current/WaterFlowError/WaterFlowErrorTypeId.enum"
import eightDirectionCharsLowerCase from "../../lib/core/direction/eightDirectionCharsLowerCase"
import eightDirectionCurrents from "../../lib/core/direction/eightDirectionCurrents"
import normalizeCircularOffset from "../../lib/core/direction/normalizeCircularOffset"
import isEntityLayerIdTerrain1 from "../../lib/core/entity/EntityLayerId/isEntityLayerIdTerrain1"
import isEntityWater from "../../lib/core/entity/isEntityWater"
import type { EntityWaterWithCurrent } from "../../lib/core/entity/isEntityWaterWithCurrent"
import oneAndNegOne from "../../lib/ldsh/oneAndNegOne"
import createSharedCachedFactory from "./createSharedCachedFactory"
import { createSharedTileGraphNodeCollectionMemo } from "./createSharedTileGraphNodeCollectionMemo"
import useGridWidthHeight from "./useGridWidthHeight"
import { useSharedWaterEntsWithCurrentListMemo } from "./useSharedWaterEntsWithCurrentListMemo"
import { useSharedWaterFlowErrorsMapMemo } from "./useSharedWaterFlowErrorsMapMemo"

export type WaterFlowNode = {
  ent: EntityWaterWithCurrent
  current: typeof eightDirectionCurrents[number]
  corrected?: boolean
}

// return all river water ents
export const useSharedWaterFlowNodeMapMemo = createSharedCachedFactory((engine) => {
  const getGridSize = useGridWidthHeight(engine)
  // if a river is an exit, it should point in or out at the sea/lake/swamp/marsh
  const getTileGraphNodesList = createSharedTileGraphNodeCollectionMemo(engine)
  const waterFlowErrorsMap = useSharedWaterFlowErrorsMapMemo(engine)

  const getWaterEntsWithCurrentList = useSharedWaterEntsWithCurrentListMemo(engine)

  const map = new ReactiveMap<number, WaterFlowNode>()

  createEffect(() => {
    const { width: gridWidth } = getGridSize()
    const tpNodeList = getTileGraphNodesList()
    const waterEntsWithCurrentList = getWaterEntsWithCurrentList()

    batch(() => {
      map.clear()
      for (const ent0 of waterEntsWithCurrentList) {
        const nodeIndex0 = ent0.x + ent0.y * gridWidth
        const { current } = ent0
        const waterFlowError = waterFlowErrorsMap.get(nodeIndex0)

        // if (nodeIndex0 === 288) {
        //   console.log('waterFlowError', nodeIndex0, deepClone({waterFlowError, ent0}))
        // }
        if (waterFlowError) {
          // if pointing directly at land, ignore
          // if (waterFlowError.type === WaterFlowErrorTypeId.PointingAtLand) {
          //   console.log('PointingAtLand', deepClone(ent0))
          // }
          if (waterFlowError.type === WaterFlowErrorTypeId.PointingAtLand || waterFlowError.type === WaterFlowErrorTypeId.PointingAtLandDiagonal) {
            const node0 = tpNodeList[nodeIndex0]
            // console.log('PointingAtLandDiagonal', deepClone(ent0))
            // if land is blocking, try rotating the current 45 degrees
            for (const offset of oneAndNegOne) {
              const edcIndex0 = current.edc
              const edcIndex1 = normalizeCircularOffset(edcIndex0 + offset, 8)
              const dirChar1 = eightDirectionCharsLowerCase[edcIndex1]
              // const nodeIndex1 = nodeIndex0 + offset
              const node1 = node0[dirChar1]
              if (node1) {
                const ent1 = node1.ents().find(isEntityLayerIdTerrain1)
                if (ent1) {
                  if (isEntityWater(ent1)) {
                    // console.log('wfn.corrected', wfn)
                    const edc1 = normalizeCircularOffset(current.edc + offset, 8)
                    map.set(nodeIndex0, {
                      ent: ent0,
                      current: eightDirectionCurrents[edc1],
                      corrected: true,
                    })
                    break
                  }
                }
              }
            }
          }
        } else {
          // use the default one
          map.set(nodeIndex0, {
            ent: ent0,
            current: eightDirectionCurrents[current.edc],
          })
        }
      }
    })
  })

  // if (DEV) {
  //   createEffect(() => {
  //     console.log('useSharedWaterFlowNodeListMemo')
  //     console.table([...map.values()])
  //   })
  // }

  return map
})
